import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Download, FileUp, Search, Settings2, Users, X } from 'lucide-react'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { PageHeader } from '@/components/shared/page-header'
import { CustomersTabs } from './customers-tabs'
import { openStageManager } from '@/stores/stage-manager-store'
import { Pagination } from '@/components/shared/pagination'
import { EmptyState, Loading } from '@/components/shared/feedback'
import { StageBadge } from '@/components/shared/stage-badge'
import { TagChip } from '@/components/shared/tag-chip'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Checkbox } from '@/components/ui/misc'
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn, initials } from '@/lib/utils'
import { apiError } from '@/lib/api-client'
import { currentLocale } from '@/lib/i18n'
import { formatVnd } from '@/lib/format-money'
import { useAuthStore } from '@/stores/auth-store'
import { useContacts, downloadContactsCsv, type ContactListItem } from '@/hooks/use-contacts'
import { useSegments } from '@/hooks/use-cdp'
import { useZaloAccounts } from '@/hooks/use-integrations'
import { useLifecycleStages } from '@/hooks/use-lifecycle-stages'
import { useMyPermissions, useOrgMembers, useTags } from '@/hooks/use-settings'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { ContactImportDialog } from './contact-import-dialog'
import { ContactQuickView } from './contact-quick-view'
import { CustomersBulkBar } from './customers-bulk-bar'
import { CustomerCards, StageChips } from './customer-cards'

const LIMIT = 20
const ALL = '__all__'

/**
 * Danh sách khách hàng. Luồng: lọc (giai đoạn, nhãn, phụ trách, phân khúc, kênh) → chọn nhiều để gắn nhãn / đổi giai đoạn /
 * giao phụ trách → bấm tên để Xem nhanh ngay tại danh sách → "Mở đầy đủ" sang trang chi tiết.
 */
export function CustomersPage() {
  const { t } = useTranslation('custList')
  const [importOpen, setImportOpen] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  // Link cũ /customers/import chuyển về /customers?import=1 → mở hộp thoại nhập.
  useEffect(() => {
    if (searchParams.get('import') !== '1') return
    setImportOpen(true)
    setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams])

  const { stages } = useLifecycleStages()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const role = useAuthStore((s) => s.user?.role)
  const canConfigure = role === 'owner' || role === 'admin'
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [stage, setStage] = useState<string>(ALL)
  const [zaloAccount, setZaloAccount] = useState<string>(ALL)
  const [tagFilter, setTagFilter] = useState<string[]>([])
  const [assignee, setAssignee] = useState<string>(ALL)
  const [segment, setSegment] = useState<string>(ALL)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [quickId, setQuickId] = useState<string | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 400)
    return () => clearTimeout(timer)
  }, [searchInput])

  const filters = useMemo(
    () => ({
      search: search || undefined,
      channelAccountId: zaloAccount === ALL ? undefined : zaloAccount,
      tag: tagFilter.length ? tagFilter.join(',') : undefined,
      assignedUserId: assignee === ALL ? undefined : assignee,
      segmentId: segment === ALL ? undefined : segment,
    }),
    [search, zaloAccount, tagFilter, assignee, segment],
  )
  const params = useMemo(
    () => ({ page, limit: LIMIT, ...filters, lifecycleStage: stage === ALL ? undefined : stage }),
    [page, filters, stage],
  )

  const { data, isLoading, isError } = useContacts(params)
  const { data: zaloAccounts } = useZaloAccounts('personal')
  const { data: perms } = useMyPermissions()
  const { data: tags = [] } = useTags()
  const { data: members = [] } = useOrgMembers(!isMobile && role !== 'member')
  const { data: segData } = useSegments()
  const rows = useMemo(() => data?.contacts ?? [], [data])
  const quick = rows.find((c) => c.id === quickId) ?? null
  const filtered = stage !== ALL || zaloAccount !== ALL || tagFilter.length > 0 || assignee !== ALL || segment !== ALL || !!search

  const resetPage = () => {
    setPage(1)
    setSelected(new Set())
  }
  const clearFilters = () => {
    setSearchInput('')
    setStage(ALL)
    setZaloAccount(ALL)
    setTagFilter([])
    setAssignee(ALL)
    setSegment(ALL)
    resetPage()
  }

  const [exporting, setExporting] = useState(false)
  const onExport = async () => {
    setExporting(true)
    try {
      const truncated = await downloadContactsCsv({ ...filters, lifecycleStage: stage === ALL ? undefined : stage })
      if (truncated) toast.warning(t('list.exportTruncated'))
      else toast.success(t('list.exported'))
    } catch (e) {
      toast.error(apiError(e))
    } finally {
      setExporting(false)
    }
  }

  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id))
  const toggleAll = () => setSelected(allOnPage ? new Set() : new Set(rows.map((r) => r.id)))
  const toggleOne = (id: string) =>
    setSelected((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })

  return (
    <div className={isMobile ? 'space-y-3' : 'space-y-5'}>
      {!isMobile && <CustomersTabs />}
      {isMobile ? (
        <h1 className="text-xl font-semibold">{t('list.title')}{data ? <span className="ml-2 text-sm font-normal text-muted-foreground">{data.total}</span> : null}</h1>
      ) : (
        <PageHeader
          title={t('list.title')}
          description={`${t('list.description')}${data ? ` ${t(filtered ? 'list.totalFiltered' : 'list.total', { count: data.total, total: data.total.toLocaleString(currentLocale()) })}` : ''}`}
          actions={
            <div className="flex gap-2">
              {perms?.has('contacts.export') && (
                <Button variant="outline" onClick={onExport} disabled={exporting} title={t('list.exportTitle')}>
                  <Download /> {exporting ? t('list.exporting') : t('list.export')}
                </Button>
              )}
              {perms?.has('contacts.import') && (
                <Button variant="outline" onClick={() => setImportOpen(true)}>
                  <FileUp /> {t('list.import')}
                </Button>
              )}
            </div>
          }
        />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder={t('list.searchPlaceholder')} className="pl-9" />
        </div>
        {isMobile && <StageChips value={stage} onChange={(v) => { setStage(v); resetPage() }} />}

        {!isMobile && (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={cn(tagFilter.length > 0 && 'border-primary text-primary')}>
                  {t('list.tags')}{tagFilter.length ? ` (${tagFilter.length})` : ''} ▾
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-72 w-56 overflow-y-auto">
                {tags.length === 0 && <p className="px-2 py-2 text-xs text-muted-foreground">{t('list.noTags')}</p>}
                {tags.map((tag) => (
                  <DropdownMenuCheckboxItem
                    key={tag.id}
                    checked={tagFilter.includes(tag.name)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(c) => {
                      setTagFilter((cur) => (c ? [...cur, tag.name] : cur.filter((x) => x !== tag.name)))
                      resetPage()
                    }}
                  >
                    <TagChip name={tag.name} />
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {role !== 'member' && (
              <Select value={assignee} onValueChange={(v) => { setAssignee(v); resetPage() }}>
                <SelectTrigger className="w-[170px]"><SelectValue placeholder={t('list.assignee')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t('list.allAssignees')}</SelectItem>
                  {members.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.fullName ?? t('list.staff')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {(segData?.segments.length ?? 0) > 0 && (
              <Select value={segment} onValueChange={(v) => { setSegment(v); resetPage() }}>
                <SelectTrigger className="w-[190px]"><SelectValue placeholder={t('list.segment')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>{t('list.allSegments')}</SelectItem>
                  {segData!.segments.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </>
        )}

        {(zaloAccounts?.length ?? 0) > 0 && (
          <Select value={zaloAccount} onValueChange={(v) => { setZaloAccount(v); resetPage() }}>
            <SelectTrigger className={isMobile ? 'w-full' : 'w-[200px]'}><SelectValue placeholder={t('list.zaloAccount')} /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('list.allZaloAccounts')}</SelectItem>
              {(zaloAccounts ?? []).map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.displayName || a.phone || t('list.zaloAccount')}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {filtered && !isMobile && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="h-4 w-4" /> {t('common:actions.clearFilter')}
          </Button>
        )}
      </div>

      {!isMobile && (
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t('list.filterByStage')}>
          <StageFilterChip active={stage === ALL} onClick={() => { setStage(ALL); resetPage() }} label={t('common:misc.all')} count={sum(data?.stageCounts)} />
          {stages.map((s) => (
            <StageFilterChip
              key={s.key}
              active={stage === s.key}
              onClick={() => { setStage(s.key); resetPage() }}
              label={s.label}
              color={s.color}
              count={data?.stageCounts?.[s.key] ?? 0}
            />
          ))}
          {canConfigure && (
            <button type="button" onClick={openStageManager} className="ml-1 inline-flex items-center gap-1 text-xs text-primary hover:underline">
              <Settings2 className="h-3.5 w-3.5" /> {t('list.setupPipeline')}
            </button>
          )}
        </div>
      )}

      {!isMobile && selected.size > 0 && (
        <CustomersBulkBar ids={[...selected]} canAssign={role !== 'member'} onClear={() => setSelected(new Set())} />
      )}

      {isMobile ? (
        <CustomerCards
          rows={rows}
          loading={isLoading}
          emptyText={isError ? t('common:status.loadError') : t('list.empty')}
          onOpen={(c) => navigate(`/customers/${c.id}`)}
        />
      ) : isLoading ? (
        <Loading label={t('common:status.loading')} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Users}
          title={isError ? t('common:status.loadError') : filtered ? t('list.emptyFiltered') : t('list.empty')}
          description={filtered ? t('list.emptyFilteredHint') : t('list.emptyHint')}
        />
      ) : (
        <div className="w-full overflow-x-auto rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr className="border-b text-left">
                <th className="w-10 px-3 py-3">
                  <Checkbox checked={allOnPage} onCheckedChange={toggleAll} aria-label={t('list.selectAllOnPage')} />
                </th>
                <Th>{t('list.col.customer')}</Th>
                <Th>{t('list.col.phone')}</Th>
                <Th>{t('list.col.stage')}</Th>
                <Th>{t('list.col.assignee')}</Th>
                <Th>{t('list.col.lastActivity')}</Th>
                <Th right>{t('list.col.spent')}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <ContactRow key={c.id} c={c} checked={selected.has(c.id)} active={quickId === c.id} onCheck={() => toggleOne(c.id)} onOpen={() => setQuickId(c.id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!!data && data.total > 0 && <Pagination page={page} limit={LIMIT} total={data.total} onPageChange={(p) => { setPage(p); setSelected(new Set()) }} />}

      <ContactQuickView contact={quick} onClose={() => setQuickId(null)} />
      <ContactImportDialog open={importOpen} onOpenChange={setImportOpen} />
    </div>
  )
}

function sum(m?: Record<string, number>): number {
  return m ? Object.values(m).reduce((a, b) => a + b, 0) : 0
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return <th className={cn('px-4 py-3 text-sm font-medium', right && 'text-right')}>{children}</th>
}

function StageFilterChip({ active, onClick, label, count, color }: { active: boolean; onClick: () => void; label: string; count: number; color?: string | null }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors',
        active ? 'border-primary bg-primary/10 font-semibold text-primary' : 'text-muted-foreground hover:bg-muted',
      )}
    >
      {color && <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />}
      {label}
      <span className="tabular-nums opacity-70">{count.toLocaleString(currentLocale())}</span>
    </button>
  )
}

function ContactRow({ c, checked, active, onCheck, onOpen }: { c: ContactListItem; checked: boolean; active: boolean; onCheck: () => void; onOpen: () => void }) {
  const { t } = useTranslation('custList')
  const name = c.crmName || c.fullName || t('list.noName')
  return (
    <tr className={cn('cursor-pointer border-b last:border-0 hover:bg-muted/50', (checked || active) && 'bg-primary/5')} onClick={onOpen}>
      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={checked} onCheckedChange={onCheck} aria-label={t('list.selectOne', { name })} />
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            {c.avatarUrl && <AvatarImage src={c.avatarUrl} alt={name} />}
            <AvatarFallback>{initials(name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="truncate font-medium">{name}</p>
            {c.tags?.length > 0 && (
              <div className="mt-0.5 flex flex-wrap gap-1">
                {c.tags.slice(0, 3).map((tag) => <TagChip key={tag} name={tag} />)}
                {c.tags.length > 3 && <span className="text-[11px] text-muted-foreground">+{c.tags.length - 3}</span>}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 tabular-nums">{c.phone || '—'}</td>
      <td className="px-4 py-3"><StageBadge stage={c.lifecycleStage} /></td>
      <td className="px-4 py-3 text-muted-foreground">{c.assignedUser?.fullName || '—'}</td>
      <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{c.lastActivity ? dayjs(c.lastActivity).format('DD/MM/YYYY') : '—'}</td>
      <td className="px-4 py-3 text-right tabular-nums">{c.totalSpent ? formatVnd(c.totalSpent) : '—'}</td>
    </tr>
  )
}
